import { useCallback, useEffect, useState } from "react"
import { useRouteMatch } from "react-router-dom"
import { RoutesPathnames } from "./types"

type RouteLink = {
  to: string
  isLast: boolean
  name: string
  isDisabled: boolean
  isGuarded: boolean
}

type CurrentRoute = {
  name: string
  isGuarded: boolean
}

type UseBreadcrumbsType = {
  links: RouteLink[]
  currentRoute: CurrentRoute
}

export const useBreadcrumbs = <T extends { [K in keyof T]?: string }>(
  baseUrl: string,
  routesPathnames: RoutesPathnames[]
): UseBreadcrumbsType => {
  const match = useRouteMatch<T>()
  const [links, setLinks] = useState<RouteLink[]>([])
  const [currentRoute, setCurrentRoute] = useState<CurrentRoute>(null)

  const { params } = match

  const getLinks = useCallback(
    (): RouteLink[] =>
      Object.keys(params)
        .filter(key => key !== "program" && key !== "page")
        .map((urlPart, index) => {
          const pathnames = Object.values<string>(params).filter(
            (pathname: string) => !baseUrl.includes(pathname)
          )
          const isLast = index === pathnames.filter(value => value).length - 1
          const to = `${baseUrl ? `/${baseUrl}` : ""}/${pathnames
            .slice(0, index + 1)
            .join("/")}`

          const routePathname = routesPathnames
            .filter(route => !route.isRoot)
            .find(pathname => pathname.path === params[urlPart])

          if (!routePathname) return

          return {
            to,
            isLast,
            name: routePathname.name,
            isDisabled: routePathname.isDisabled,
            isGuarded: routePathname.isGuarded
          }
        }),
    [routesPathnames, params, baseUrl]
  )

  useEffect(() => {
    if (!routesPathnames) return

    const routeLinks = getLinks()

    const currentRouteLink = routeLinks.find(link => link?.isLast)

    setLinks(routeLinks)
    currentRouteLink &&
      setCurrentRoute({
        name: currentRouteLink.name,
        isGuarded: currentRouteLink.isGuarded
      })
  }, [getLinks])

  return { links, currentRoute }
}
